import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePages } from "@/hooks/usePages";
import { WebBuilderPreloader } from "@/components/webbuilder/ui/loaders/WebBuilderPreloader";
import { Breadcrumb } from "@/components/common/Breadcrumb";

export default function WebBuilderPage() {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const {
    pages,
    loading: pagesLoading,
    pagesFetched,
    error: pagesError,
    pagination,
    actions,
  } = usePages();

  const hasRedirected = useRef(false);

  const breadcrumbItems = [
    {
      label: "Website Builder",
      isActive: true,
    },
  ];

  // Fetch pages on component mount - only once
  useEffect(() => {
    // We only need count for routing; a single item fetch is enough and faster.
    actions.fetchPages(1, 1);
  }, []); // Empty deps - only run once

  // Redirect based on pages availability - only redirect once
  useEffect(() => {
    if (hasRedirected.current) return; // Already redirected

    if (!storeType || !storeId) return;
    if (!pagesFetched || pagesLoading || pagesError) return;

    if (!pagesLoading && !pagesError && storeType && storeId) {
      hasRedirected.current = true; // Mark as redirected

      if ((pagination?.total ?? pages.length) === 0) {
        // No pages exist, redirect to import
        navigate(`/${storeType}/${storeId}/webbuilder/onboarding`, {
          replace: true,
        });
      } else {
        // Pages exist, redirect to edit
        navigate(`/${storeType}/${storeId}/webbuilder/edit`, { replace: true });
      }
    }
  }, [
    pages,
    pagesFetched,
    pagesLoading,
    pagesError,
    pagination,
    navigate,
    storeType,
    storeId,
  ]);

  // Show loading state while determining route
  if (!pagesFetched || pagesLoading) {
    return (
      <div className="p-6 space-y-6">
        <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        <WebBuilderPreloader message="Checking your website pages..." />
      </div>
    );
  }

  // Show error state
  if (pagesError) {
    return (
      <div className="p-6 space-y-6">
        <Breadcrumb items={breadcrumbItems} showBackButton={false} />
        <div className="h-64 w-full bg-background dark:bg-auth-background flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">Failed to load pages</p>
            <button
              onClick={() => actions.fetchPages(1, 1)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
